import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { TimelineRow, ProductState, STATE_COLORS } from '@/types/promoAttribution';
import { DAY_WINNER_MIN_UNIT_SHARE } from '@/types/promoAttribution';
import { formatPromoNumber } from '@/utils/promoFormatNumber';

interface Props {
  data: TimelineRow[];
}

function getState(row: TimelineRow): ProductState {
  if (row.winning_state) return row.winning_state;
  if (row.has_discount && row.has_promo_data) return 'Promotion and Promo Data';
  if (!row.has_discount && row.has_promo_data) return 'Promo Data';
  if (row.has_discount && !row.has_promo_data) return 'Promotion';
  return 'Standard Price';
}

 
const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  const row: TimelineRow = payload[0]?.payload;
  const state = getState(row);
  const pct = (v: number | null | undefined) =>
    v == null || Number.isNaN(v) ? '—' : `${formatPromoNumber(v, 1)}%`;
  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: 8,
        padding: '10px 14px',
        fontSize: 12,
        color: '#0f172a',
        maxWidth: 260,
      }}
    >
      <p style={{ fontWeight: 600, marginBottom: 4 }}>{label}</p>
      <p>Units sold: <strong>{formatPromoNumber(row.units_sold)}</strong></p>
      <p style={{ color: STATE_COLORS[state] ?? '#64748b', fontWeight: 500 }}>
        Day label: {state}
        {row.top_state_share_pct != null && state !== 'Mixed' && (
          <span style={{ fontWeight: 400, color: '#64748b' }}> ({formatPromoNumber(row.top_state_share_pct, 1)}% of units)</span>
        )}
      </p>
      {state === 'Mixed' && row.top_state_share_pct != null && (
        <p style={{ color: '#64748b', fontSize: 11, marginTop: 2 }}>
          No single state reached {Math.round(DAY_WINNER_MIN_UNIT_SHARE * 100)}% of units (top had {formatPromoNumber(row.top_state_share_pct, 1)}%).
        </p>
      )}
      <p style={{ color: '#64748b', marginTop: 6, fontSize: 11, lineHeight: 1.45 }}>
        Unit mix: Std {pct(row.pct_standard)} · Promo {pct(row.pct_promotion)} ·
        Promo Data {pct(row.pct_promo_data)} · Both {pct(row.pct_both)}
      </p>
      <p style={{ color: '#94a3b8', marginTop: 4, fontSize: 10, lineHeight: 1.35 }}>
        Promotion = line price discount; Promo Data = attributed rule without that discount signal.
      </p>
      {row.active_rule_titles && (
        <p style={{ color: '#64748b', marginTop: 4, lineHeight: 1.4 }}>
          {row.active_rule_titles}
        </p>
      )}
    </div>
  );
};

export function PromoProductTimeline({ data }: Props) {
  return (
    <ResponsiveContainer width="100%" height={320}>
      <BarChart data={data} margin={{ top: 4, right: 16, bottom: 24, left: 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
        <XAxis
          dataKey="sale_date"
          tick={{ fontSize: 10, fill: '#94a3b8' }}
          interval="preserveStartEnd"
          tickLine={false}
          axisLine={false}
        />
        <YAxis
          tick={{ fontSize: 11, fill: '#64748b' }}
          tickLine={false}
          axisLine={false}
          width={40}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8, color: '#64748b' }} />
        <Bar dataKey="units_sold" name="Units Sold" radius={[3, 3, 0, 0]} maxBarSize={24}>
          {data.map((row, i) => (
            <Cell key={i} fill={STATE_COLORS[getState(row)]} fillOpacity={0.9} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
